import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { FC } from 'react'

interface NumberBoxProps {
  /**
   * `num` is the number to display. Each digit will render as a separate box.
   * @type {number}
   * @defaultValue 0
   */
  num?: number

  /**
   * `label` is the label for the number box, if any.
   * @type {string}
   * @defaultValue ''
   */
  label?: string
}

/**
 * `NumberBox` is a React functional component displaying a number as one digit per box.
 * @param {NumberBoxProps} props
 */
const NumberBox: FC<NumberBoxProps> = ({ num = 0, label = '' }: NumberBoxProps) => {
  const theme = useTheme()
  const numString = num.toString().padStart(2, '0')
  return (
    <Box
      aria-label={`${numString} ${label}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {numString.split('').map((digit, index) => (
          <Box
            key={index}
            sx={{
              width: '4rem',
              height: '5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: index > 0 ? 0.75 : 0,
              borderRadius: 0.5,
              backgroundColor: theme.palette.grey[300],
              [theme.breakpoints.down('sm')]: {
                width: '3.5rem',
              },
            }}
          >
            <Typography
              variant="h3"
              component="span"
              sx={{
                fontSize: '2.9375rem',
                [theme.breakpoints.down('lg')]: {
                  fontSize: '2.9375rem',
                },
              }}
            >
              {digit}
            </Typography>
          </Box>
        ))}
      </Paper>
      <Typography variant="captionMedium" sx={{ pt: 1.5 }}>
        {label}
      </Typography>
    </Box>
  )
}

export default NumberBox
